import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import userService from "../../api/services/userService";

export function useMatchUser() {
  const [senderEmail, setSenderEmail] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [senderUser, setSenderUser] = useState(null);
  const [receiverUser, setReceiverUser] = useState(null);
  const [fetchingSender, setFetchingSender] = useState(false);
  const [fetchingReceiver, setFetchingReceiver] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleFetchSender = useCallback(async () => {
    if (!senderEmail.trim()) {
      toast.error("Please enter sender email");
      return;
    }
    setFetchingSender(true);
    setSenderUser(null);
    setResult(null);
    try {
      const response = await userService.fetchUserByEmail(senderEmail);
      if (response.user) {
        setSenderUser(response.user);
        toast.success("Sender user fetched successfully!");
      } else {
        toast.error("Sender user not found");
      }
    } catch (error) {
      console.error("Error fetching sender:", error);
      toast.error(error?.message || "Failed to fetch sender user. Please try again.");
    } finally {
      setFetchingSender(false);
    }
  }, [senderEmail]);

  const handleFetchReceiver = useCallback(async () => {
    if (!receiverEmail.trim()) {
      toast.error("Please enter receiver email");
      return;
    }
    setFetchingReceiver(true);
    setReceiverUser(null);
    setResult(null);
    try {
      const response = await userService.fetchUserByEmail(receiverEmail.trim());
      if (response.user) {
        setReceiverUser(response.user);
        toast.success("Receiver user fetched successfully!");
      } else {
        toast.error("Receiver user not found");
      }
    } catch (error) {
      console.error("Error fetching receiver:", error);
      toast.error(error?.message || "Failed to fetch receiver user. Please try again.");
    } finally {
      setFetchingReceiver(false);
    }
  }, [receiverEmail]);

  const handleOpenConfirmModal = useCallback(() => {
    if (!senderUser || !receiverUser) {
      toast.error("Please fetch both sender and receiver users first");
      return;
    }
    if (senderUser.id === receiverUser.id) {
      toast.error("Sender and receiver cannot be the same user");
      return;
    }
    setShowConfirmModal(true);
  }, [senderUser, receiverUser]);

  const handleSendNotification = useCallback(async () => {
    setShowConfirmModal(false);
    setSending(true);
    setResult(null);
    try {
      const response = await userService.sendNotificationToFilteredUser(
        senderUser.id,
        receiverUser.id
      );
      setResult({
        success: true,
        message: response.message || "Notification sent successfully!",
        data: response.data,
      });
      toast.success("Notification sent successfully!");
      setSenderEmail("");
      setReceiverEmail("");
      setSenderUser(null);
      setReceiverUser(null);
    } catch (error) {
      console.error("Error sending notification:", error);
      setResult({
        success: false,
        message: error?.message || "Failed to send notification",
      });
      toast.error(error?.message || "Failed to send notification");
    } finally {
      setSending(false);
    }
  }, [senderUser, receiverUser]);

  const handleReset = useCallback(() => {
    setSenderEmail("");
    setReceiverEmail("");
    setSenderUser(null);
    setReceiverUser(null);
    setResult(null);
  }, []);

  return {
    senderEmail,
    setSenderEmail,
    receiverEmail,
    setReceiverEmail,
    senderUser,
    receiverUser,
    fetchingSender,
    fetchingReceiver,
    sending,
    result,
    showConfirmModal,
    setShowConfirmModal,
    handleFetchSender,
    handleFetchReceiver,
    handleOpenConfirmModal,
    handleSendNotification,
    handleReset,
  };
}
